import { useEffect, useState } from "react";
// REDUX
import { useSelector } from "react-redux";
import { useGetServersLightQuery } from "../../redux/servers/serverAPI";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";
// INTERFACE
import { ServerLightInterface } from "../../interfaces/Server.interface";
import { RootState } from "../../app/store";

/**
 * Fetches the minimal edge-server projection via `useGetServersLightQuery`.
 *
 * Resolves a JWT first and passes it to the query, which stays skipped until the
 * token arrives. Scoped to the globally selected organisation unless overridden.
 */
export default function useGetAllServersLight(overrideOrgId?: string) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [servers, setServers] = useState<ServerLightInterface[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [errorObject, setErrorObject] = useState<any>({});
  const [errorMessage, setErrorMessage] = useState<string>("Error");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { globalOrganizationId } = useSelector((state: RootState) => state.home);
  const orgId = overrideOrgId || globalOrganizationId;

  // ************************************************ */
  // FUNCTIONS ************************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const { data, isLoading, isError, isFetching, error } = useGetServersLightQuery(
    { token, orgId },
    { skip: !token, refetchOnMountOrArgChange: false },
  );

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  useEffect(() => {
    if (data && data.data) {
      setServers(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setErrorObject(error);
    }
  }, [error]);

  useEffect(() => {
    if (errorObject && errorObject.data && errorObject.data.message) {
      setErrorMessage(errorObject.data.message);
    }
  }, [errorObject]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return {
    serversLight: servers,
    isLoadingGetAllServersLight: isLoading || isFetching,
    errorMessageGetAllServersLight: errorMessage,
    isErrorGetAllServersLight: isError,
    totalRowsGetAllServersLight: data?.total ?? 0,
  };
}
